/* eslint-disable no-console */
import { useState, useMemo, useEffect } from "react";
import { Button, Modal } from "react-bootstrap";

import { addClickRecord, getToken } from "../../../Utils/helper";
import api, { api_without_cancellation } from "../../../api/api";

import CallStatusModal from "./CallStatusModal";

const AddPhoneCallModal = ({ handleClose, show, clientData, clientId, caseId }) => {
  const [phoneLog, setPhoneLog] = useState({
    client_id: clientId,
    case_id: caseId,
    contact_id: null,
    description: "",
    call_status: "",
  });

  const [loading, setLoading] = useState(false);
  const [callStatusModal, setCallStatusModal] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [latestWsEvent, setLatestWsEvent] = useState(null);

  const token = getToken();
  console.log(clientData);
  // ── Contacts ────────────────────────────────────────────────────────────────
  const contactOptions = useMemo(() => {
    const contacts = [];
    ["contact_1", "contact_2", "contact_3"].forEach((key) => {
      const c = clientData?.[key];
      if (c?.phone_number) {
        contacts.push({
          id: c.id,
          phone_number: c.phone_number,
          label:
            c.first_name && c.last_name
              ? `${c.first_name} ${c.last_name} - ${c.phone_number}`
              : c.phone_number,
        });
      }
    });
    return contacts;
  }, [clientData]);

  const contactOptions2 = useMemo(() => {
    const contacts = [];
    ["emergency_contact"].forEach((key) => {
      const c = clientData?.[key];
      if (c?.contact?.phone_number) {
        contacts.push({
          id: c.id,
          phone_number: c.contact?.phone_number,
          label:
            c.first_name && c.last_name
              ? `${c.first_name} ${c.last_name} - ${c.contact?.phone_number}`
              : c.contact?.phone_number,
        });
      }
    });
    return contacts;
  }, [clientData]);

  const contactOptions3 = useMemo(() => {
    const contacts = [];
    ["spouse"].forEach((key) => {
      const c = clientData?.[key];
      if (c?.contact?.phone_number) {
        contacts.push({
          id: c.id,
          phone_number: c.contact?.phone_number,
          label:
            c.first_name && c.last_name
              ? `${c.first_name} ${c.last_name} - ${c.contact?.phone_number}`
              : c.contact?.phone_number,
        });
      }
    });
    return contacts;
  }, [clientData]);

  // Pre-select primary phone
  useEffect(() => {
    if (!clientData?.primary_phone?.id) return;
    const match = [clientData.contact_1, clientData.contact_2, clientData.contact_3].find(
      (c) => c?.id === clientData.primary_phone.id
    );
    if (match) setPhoneLog((prev) => ({ ...prev, contact_id: match.id }));
  }, [clientData]);

  const handleContactChange = (e) => {
    const contactId = e.target.value ? parseInt(e.target.value, 10) : null;
    setPhoneLog({ ...phoneLog, contact_id: contactId });
  };

  // ── Log call (manual entry) ─────────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post(
        "/api/client-page/communication/call-log/add/",
        {
          client_id: clientId,
          case_id: caseId,
          contact_id: phoneLog.contact_id,
          description: phoneLog.description,
          text: phoneLog.call_status,
        },
        { headers: { Authorization: token } }
      );
      handleClose();
    } catch (err) {
      console.error(err.response?.data?.message || "Failed to create phone call log");
    } finally {
      setLoading(false);
    }
  };

  // ── Initiate outbound call ──────────────────────────────────────────────────
  const handleCall = async () => {
    const pN = contactOptions.find((c) => c.id === phoneLog.contact_id);
    setCallStatusModal(true);
    setCallActive(true);
    setLatestWsEvent(null);
    console.log(pN);
    try {
      await api_without_cancellation.post("/api/client-page/client-communication/calls/initiate/", {
        client_id: clientId,
        case_id: caseId,
        selected_phone_number: pN?.phone_number,
      });
    } catch (err) {
      console.error("Failed to initiate call:", err);
    }
  };

  // ── WebSocket event listener ────────────────────────────────────────────────
  useEffect(() => {
    const handleGlobalWebSocketMessage = (event) => {
      const message = event.detail;
      if (message?.type !== "outbound_call_popup_status") return;

      const data = message?.data;
      if (!data) return;

      console.log("📨 Call status event:", data);

      setLatestWsEvent(data);

      const terminalEvents = ["CLIENT_CALL_COMPLETED", "CLIENT_CALL_FAILED", "CLIENT_CALL_BUSY"];
      if (terminalEvents.includes(data.event)) {
        setCallActive(false);
      }
    };

    window.addEventListener("clientCommunicationMessage", handleGlobalWebSocketMessage);
    return () =>
      window.removeEventListener("clientCommunicationMessage", handleGlobalWebSocketMessage);
  }, [clientId, caseId]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        aria-labelledby="chatModalLabel"
        centered
        dialogClassName="modal-dialog modal-dialog-centered"
        style={{ opacity: callStatusModal ? 0.7 : 1 }}
      >
        <Modal.Header
          style={{ borderBottom: "0px" }}
          className="modal-header text-center p-0 bg-primary popup-heading-color justify-content-center"
        >
          <h5 className="modal-title d-flex align-items-center justify-content-center font-size-20 height-25 font-weight-semibold text-uppercase popup-heading-color font-weight-500">
            New Client Phone Call
          </h5>
        </Modal.Header>

        <Modal.Body className="p-0 m-t-5 m-b-5">
          <div style={{ minHeight: "200px" }} className="p-0">
            {/* Call outcome */}
            <div className="row mx-auto align-items-start m-b-5 mb-3">
              <div className="col-md-12 p-l-5 p-r-5 d-flex flex-wrap align-items-center gap-3">
                {[
                  ["Client Answered", "Client Answered"],
                  ["Voicemail Left", "Voicemail Left"],
                  ["No Answer or Voicemail", "No Answer or Voicemail"],
                ].map(([value, label]) => (
                  <label key={value} className="d-flex align-items-center mb-0">
                    <input
                      type="radio"
                      name="call_status"
                      value={value}
                      checked={phoneLog.call_status === value}
                      onChange={(e) =>
                        setPhoneLog((prev) => ({ ...prev, call_status: e.target.value }))
                      }
                      className="me-1"
                    />
                    <span style={{ fontSize: "14px" }}>{label}</span>
                  </label>
                ))}
              </div>
            </div>
            {/* Phone number selection */}
            <div className="row mx-auto align-items-start m-b-5 mb-3">
              <div className="col-md-5 p-l-5 pe-0 text-start">
                <span
                  className="d-inline-block"
                  style={{ color: "var(--primary-25)", fontWeight: "600" }}
                >
                  Client Phone Number
                </span>
              </div>
              <div className="col-md-7 p-l-5 p-r-5">
                {contactOptions.map((contact) => {
                  const isMain = contact.id === clientData?.primary_phone?.id;
                  return (
                    <div
                      key={contact.id}
                      className="d-flex align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleContactChange({ target: { value: contact.id } })}
                    >
                      <span
                        style={{ color: "var(--primary-25)", fontWeight: "600", minWidth: "35px" }}
                      >
                        {isMain && "Main"}
                      </span>
                      <input
                        type="radio"
                        name="phone_contact"
                        value={contact.id}
                        checked={phoneLog.contact_id === contact.id}
                        onChange={() => handleContactChange({ target: { value: contact.id } })}
                        className="ms-1 me-1"
                      />
                      <span className="m-r-5" style={{ fontSize: "14px" }}>
                        {contact.phone_number}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="row mx-auto align-items-start m-b-5 mb-3">
              <div className="col-md-5 p-l-5 pe-0 text-start">
                <span
                  className="d-inline-block"
                  style={{ color: "var(--primary-25)", fontWeight: "600" }}
                >
                  Client Emergency Contact
                </span>
              </div>
              <div className="col-md-7 p-l-5 p-r-5">
                {contactOptions2.map((contact) => {
                  return (
                    <div
                      key={contact.id}
                      className="d-flex align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleContactChange({ target: { value: contact.id } })}
                    >
                      <span
                        style={{ color: "var(--primary-25)", fontWeight: "600", minWidth: "35px" }}
                      >
                        {/*  */}
                      </span>
                      <input
                        type="radio"
                        name="phone_contact"
                        value={contact.id}
                        checked={phoneLog.contact_id === contact.id}
                        onChange={() => handleContactChange({ target: { value: contact.id } })}
                        className="ms-1 me-1"
                      />
                      <span className="m-r-5" style={{ fontSize: "14px" }}>
                        {contact.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="row mx-auto align-items-start m-b-5 mb-3">
              <div className="col-md-5 p-l-5 pe-0 text-start">
                <span
                  className="d-inline-block"
                  style={{ color: "var(--primary-25)", fontWeight: "600" }}
                >
                  Client Spouse Contact
                </span>
              </div>
              <div className="col-md-7 p-l-5 p-r-5">
                {contactOptions3.map((contact) => {
                  return (
                    <div
                      key={contact.id}
                      className="d-flex align-items-center"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleContactChange({ target: { value: contact.id } })}
                    >
                      <span
                        style={{ color: "var(--primary-25)", fontWeight: "600", minWidth: "35px" }}
                      >
                        {/*  */}
                      </span>
                      <input
                        type="radio"
                        name="phone_contact"
                        value={contact.id}
                        checked={phoneLog.contact_id === contact.id}
                        onChange={() => handleContactChange({ target: { value: contact.id } })}
                        className="ms-1 me-1"
                      />
                      <span className="m-r-5" style={{ fontSize: "14px" }}>
                        {contact.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            <div className="row mx-auto align-items-start m-b-5 mb-3">
              <div className="col-md-4 p-l-5 pe-0 text-start">
                <span
                  className="d-inline-block"
                  style={{ color: "var(--primary-25)", fontWeight: "600" }}
                >
                  Notes
                </span>
              </div>
              <div className="col-md-8 p-l-5 p-r-5">
                <textarea
                  rows="3"
                  value={phoneLog.description}
                  onChange={(e) => setPhoneLog({ ...phoneLog, description: e.target.value })}
                  style={{ height: "140px" }}
                  className="form-control textarea p-l-5 p-r-5 p-t-5 p-b-5"
                  placeholder="Enter Phone Call Notes"
                />
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer
          className="p-0 mt-0 padding-outside-btn-new-provider"
          style={{ borderTop: "none" }}
        >
          <Button
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            variant="secondary"
            onClick={() => {
              addClickRecord(999, 2);
              handleClose();
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            onClick={() => {
              addClickRecord(999, 2);
              handleSubmit();
            }}
            disabled={loading || !phoneLog?.description}
          >
            Log Client Call
          </Button>
          <Button
            variant="success"
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            onClick={() => {
              addClickRecord(999, 2);
              handleCall();
            }}
            disabled={callActive} // ← disabled while call is live
            title={callActive ? "Call already in progress" : ""}
          >
            {callActive ? "Call in Progress…" : "Call Client"}
          </Button>
        </Modal.Footer>
      </Modal>

      {callStatusModal && (
        <CallStatusModal
          show={callStatusModal}
          onClose={() => setCallStatusModal(false)}
          wsEvent={latestWsEvent}
        />
      )}
    </>
  );
};

export default AddPhoneCallModal;
